/**
 * CetusEd Admin Console — Application Entry Point
 *
 * COMPLIANCE: The admin console has elevated access to cross-district data.
 *   Authentication uses admin-specific SSO with mandatory MFA (NIST SP 800-63B
 *   AAL3 for vendor support staff). All admin actions are audit-logged with
 *   the actor's identity, district scope, action, and timestamp.
 *
 * MIGRATION TARGET: The monolith has no admin console. This is a new
 *   application for managing vendor-hosted deployments in the NextGen architecture.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { AppError } from '@cetused/shared';
import { App } from './App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      retry: 2,
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw AppError.internal('Root element #root not found in the DOM');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
